import { useState, ReactNode } from 'react';

import { Box } from '../Box';
import { Typography } from '../Typography';
import { Icon } from '../Icon';

import { useStyles } from './InfoBox.styles';

/**
 * Свойства компонента {@link InfoBox}
 */
export interface InfoBoxProps {
  /**
   * Содержимое, которое будет отображено внутри InfoBox.
   */
  children: ReactNode | ReactNode[];

  /**
   * Заголовок InfoBox.
   */
  title: string;

  /**
   * Если true, InfoBox будет развернут по умолчанию.
   *
   * @defaultValue `false`
   */
  defaultExpanded?: boolean;

  /**
   * Если true, рядом с заголовком будет отображена иконка ошибки.
   *
   * @defaultValue `false`
   */
  showIcon?: boolean;
}

/**
 * Пользовательский компонент InfoBox, который отображает сворачиваемую информационную панель с заголовком и необязательной иконкой.
 *
 * @public
 * @param children - Содержимое, которое будет отображено внутри InfoBox.
 * @param showIcon - Если true, рядом с заголовком будет отображена иконка ошибки.
 * @param title - Заголовок InfoBox.
 * @param defaultExpanded - Если true, InfoBox будет развернут по умолчанию.
 * @returns  JSX элемент, представляющий компонент InfoBox.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link InfoBoxProps}
 */
export const InfoBox = ({
  children,
  showIcon,
  title,
  defaultExpanded,
}: InfoBoxProps) => {
  const [isExpanded, setExpanded] = useState(defaultExpanded);
  const { classes } = useStyles();

  return (
    <Box
      bgcolor="bg.greyscale.primary"
      radius="l"
      display="flex"
      p="16"
      flexDirection="column"
      testId="info-box-test-id"
    >
      <Box
        width="100%"
        gap="8"
        display="flex"
        flexDirection="column"
        onClick={() => setExpanded(!isExpanded)}
        className={classes.root}
        mb={isExpanded ? '8' : '0'}
      >
        <Box
          display="flex"
          alignContent="center"
          justifyContent="space-between"
        >
          <Box gap="8" display="flex" alignItems="center" alignContent="center">
            {showIcon && (
              <Icon
                name="error_circle_outline"
                size="20"
                color="original.brand.primary"
                data-testid="error-icon-test-id"
              />
            )}
            <Typography variant="subtitle2" color="text.light.primary">
              {title}
            </Typography>
          </Box>
          <Box px="4" py="4">
            {isExpanded ? (
              <Icon
                name="arrow_triangle_up"
                size="16"
                color="original.brand.primary"
                data-testid="arrow-drop-up-icon-test-id"
              />
            ) : (
              <Icon
                name="arrow_triangle_down"
                size="16"
                color="original.brand.primary"
                data-testid="arrow-drop-down-icon-test-id"
              />
            )}
          </Box>
        </Box>
      </Box>
      {isExpanded && children}
    </Box>
  );
};
